import type { ZodSchema } from 'zod'
import type { AxiosResponse } from 'axios'
import { useAxios } from './useAxios'
import { getBackendErrorCode, handleAxiosError } from './useAxiosError'

export type ApiOk<T> = { ok: true; data: T }
export type ApiErr = { ok: false; error: { status?: number; code?: string } }
export type ApiResult<T> = ApiOk<T> | ApiErr

/**
 * Unified, typed API helper with async/await returning a discriminated union.
 * - method/url/setAuth are forwarded to useAxios
 * - optional body, optional Zod response schema (validated in useAxios)
 * - errors are logged via handleAxiosError and mapped to { ok: false, error: { status? } }
 */
export async function useApi<RequestData = never, ResponseData = RequestData>(
  method: 'GET' | 'POST' | 'DELETE' | 'HEAD' | 'OPTIONS' | 'PUT' | 'PATCH',
  url: string,
  setAuth: boolean,
  body?: RequestData,
  responseSchema?: ZodSchema<ResponseData>,
  opts?: { logPrefix?: string }
): Promise<ApiResult<ResponseData>> {
  try {
    const res: AxiosResponse<ResponseData> = await useAxios<RequestData, ResponseData>(
      method,
      url,
      setAuth,
      body as any,
      responseSchema
    )

    return { ok: true, data: res.data }
  } catch (error: unknown) {
    const { status } = handleAxiosError(error, { logPrefix: opts?.logPrefix })
    return { ok: false, error: { status, code: getBackendErrorCode(error) } }
  }
}
